import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Questionnaire from './pages/Questionnaire';
import PassportView from './pages/PassportView';
import PublicPassport from './pages/PublicPassport';
import RecruiterDashboard from './pages/RecruiterDashboard';
import AdminPanel from './pages/AdminPanel';
import PersonalDetailsForm from './pages/PersonalDetailsForm';
import CVBuilder from './pages/CVBuilder';
import CVHistory from './pages/CVHistory';
import CreditsPage from './pages/CreditsPage';
import PurchaseCredits from './pages/PurchaseCredits';
import FeedbackForm from './pages/FeedbackForm';
import { Toaster } from './components/ui/sonner';
import { DarkModeProvider } from './contexts/DarkModeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with requests
axios.defaults.withCredentials = true;

// Auth Context
export const AuthContext = React.createContext();

// Role-based route guard
const RoleGuard = ({ children, allowedRoles }) => {
  const { user } = React.useContext(AuthContext);
  
  if (!user) {
    return <Navigate to="/" />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to access this page.</p>
          <button
            onClick={() => window.location.href = '/dashboard'}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Process Google OAuth session_id from URL fragment first
    const processGoogleAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            {},
            {
              headers: { 'X-Session-ID': sessionId },
              withCredentials: true
            }
          );
          
          // Clear URL fragment
          window.history.replaceState(null, '', window.location.pathname);
          
          // Set user and token
          login(response.data.token, response.data.user);
          setLoading(false);
          return true; // Session processed
        } catch (error) {
          console.error('Google auth error:', error);
          window.history.replaceState(null, '', window.location.pathname);
        }
      }
      return false; // No session to process
    };
    
    // Always try to fetch user (works with both cookie session and JWT token)
    const checkAuth = async () => {
      // First check if we have a Google session_id to process
      const googleProcessed = await processGoogleAuth();
      
      if (googleProcessed) {
        return; // Already logged in via Google
      }
      
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/auth/me`, {
          headers,
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        // No valid session or token
        if (token) {
          localStorage.removeItem('token');
          setToken(null);
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const fetchUser = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, {
        headers,
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = async () => {
    try {
      // Call backend logout to clear session cookie
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading RefrX...</p>
        </div>
      </div>
    );
  }

  return (
    <DarkModeProvider>
      <AuthContext.Provider value={{ user, token, login, logout, fetchUser }}>
        <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
            <Route
              path="/dashboard"
              element={user ? <Dashboard /> : <Navigate to="/" />}
            />
            <Route
              path="/questionnaire"
              element={user ? <Questionnaire /> : <Navigate to="/" />}
            />
            <Route
              path="/personal-details"
              element={
                <RoleGuard allowedRoles={['candidate']}>
                  <PersonalDetailsForm />
                </RoleGuard>
              }
            />
            <Route
              path="/cv-builder"
              element={
                <RoleGuard allowedRoles={['candidate']}>
                  <CVBuilder />
                </RoleGuard>
              }
            />
            <Route
              path="/cv-history"
              element={
                <RoleGuard allowedRoles={['candidate']}>
                  <CVHistory />
                </RoleGuard>
              }
            />
            <Route
              path="/credits"
              element={
                <RoleGuard allowedRoles={['candidate']}>
                  <CreditsPage />
                </RoleGuard>
              }
            />
            <Route
              path="/purchase-credits"
              element={
                <RoleGuard allowedRoles={['candidate']}>
                  <PurchaseCredits />
                </RoleGuard>
              }
            />
            <Route
              path="/feedback"
              element={
                <RoleGuard allowedRoles={['candidate']}>
                  <FeedbackForm />
                </RoleGuard>
              }
            />
            <Route
              path="/passport"
              element={
                <RoleGuard allowedRoles={['candidate']}>
                  <PassportView />
                </RoleGuard>
              }
            />
            <Route path="/passport/:token" element={<PublicPassport />} />
            <Route
              path="/recruiter"
              element={
                <RoleGuard allowedRoles={['recruiter', 'admin']}>
                  <RecruiterDashboard />
                </RoleGuard>
              }
            />
            <Route
              path="/admin"
              element={
                <RoleGuard allowedRoles={['admin']}>
                  <AdminPanel />
                </RoleGuard>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
        </div>
      </AuthContext.Provider>
    </DarkModeProvider>
  );
}

export default App;